import {
  AlertCircle,
  CheckCircle2,
  Cpu,
  Globe,
  KeyRound,
  Loader2,
  Lock,
  Play,
  ShieldCheck,
  Sparkles,
  Terminal,
  Unlock,
} from "lucide-react";

import { AuthStrategyCard, CapabilityItem } from "../ScannerParts";
import type { ScannerController } from "../useScannerController";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const CAPABILITIES = [
  { icon: Globe, text: "Crawl every reachable page" },
  { icon: ShieldCheck, text: "Identify authentication surfaces" },
  { icon: Lock, text: "Map public and protected routes" },
  { icon: Sparkles, text: "Group pages into business modules" },
  { icon: Terminal, text: "Detect forms and interactions" },
  { icon: Cpu, text: "Build an app knowledge graph for AI tests" },
];

export default function ConnectPhase({ c }: { c: ScannerController }) {
  const onStart = () => {
    if (c.authStrategy === "none") c.startScan();
    else c.goToAuth();
  };

  const startDisabled = !c.url.trim() || !c.hasVersion || c.isStarting;

  return (
    <div className="grid gap-5 p-5 lg:grid-cols-[1.25fr_1fr]">
      <div className="space-y-5">
        <div>
          <h2 className="text-[17px] font-semibold tracking-tight">
            Point AI at your application
          </h2>
          <p className="mt-1 max-w-xl text-[13px] text-muted-foreground">
            The agent crawls every page, understands navigation, detects forms
            and authentication, and builds a live map — ready to generate tests.
          </p>
        </div>

        {!c.hasVersion && (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-[12px] text-warning">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Select a project first — a scan runs against the current project's
              version.
            </span>
          </div>
        )}

        <Card className="shadow-none p-5">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="scanner-url"
                className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              >
                Application URL
              </Label>
              <div className="relative">
                <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="scanner-url"
                  value={c.url}
                  onChange={(e) => c.changeUrl(e.target.value)}
                  className="h-10 pl-9 font-mono text-[13px]"
                  placeholder="https://app.example.com"
                />
                <UrlStatus status={c.urlValidationStatus} />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                How does the app authenticate?
              </Label>
              <div className="grid gap-2 sm:grid-cols-2">
                <AuthStrategyCard
                  active={c.authStrategy === "none"}
                  onClick={() => c.selectAuthStrategy("none")}
                  icon={Unlock}
                  title="Public"
                  desc="No login required"
                />
                <AuthStrategyCard
                  active={c.authStrategy === "form"}
                  onClick={() => c.selectAuthStrategy("form")}
                  icon={Lock}
                  title="Login form"
                  desc="Username & password"
                />
                <AuthStrategyCard
                  active={c.authStrategy === "token"}
                  onClick={() => c.selectAuthStrategy("token")}
                  icon={KeyRound}
                  title="Bearer token"
                  desc="Authorization header"
                />
                <AuthStrategyCard
                  active={c.authStrategy === "api_key"}
                  onClick={() => c.selectAuthStrategy("api_key")}
                  icon={Terminal}
                  title="API key"
                  desc="Custom header key"
                />
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-success" />
              Credentials are encrypted and used only for this scan.
            </div>
            <Button
              size="default"
              onClick={onStart}
              disabled={startDisabled}
              className="gap-1.5"
            >
              {c.isStarting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {c.authStrategy === "none" ? "Start scan" : "Continue"}
            </Button>
          </div>
        </Card>
      </div>

      <Card className="shadow-none overflow-hidden self-start">
        <div className="border-b border-border bg-gradient-to-br from-info/5 via-transparent to-transparent p-4">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-info" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-info">
              What the AI will do
            </span>
          </div>
          <p className="mt-1.5 text-[12px] text-muted-foreground">
            A single pass that learns your whole application.
          </p>
        </div>
        <ul className="divide-y divide-border">
          {CAPABILITIES.map(({ icon, text }) => (
            <CapabilityItem key={text} icon={icon} text={text} />
          ))}
        </ul>
      </Card>
    </div>
  );
}

function UrlStatus({
  status,
}: {
  status: ScannerController["urlValidationStatus"];
}) {
  if (status === "idle") return null;
  return (
    <span className="absolute right-3 top-1/2 -translate-y-1/2">
      {status === "validating" && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      {status === "accessible" && (
        <CheckCircle2 className="h-4 w-4 text-success" />
      )}
      {status === "not-accessible" && (
        <span
          className={cn(
            "inline-flex items-center gap-1 text-[11px] text-warning",
          )}
        >
          <AlertCircle className="h-3.5 w-3.5" />
        </span>
      )}
    </span>
  );
}
